import React, { useContext } from "react";
import { useState } from "react";
import { EmptyState } from "@/modules/common/components/ui-kit/empty-state";
import HouseIcon from "@/assets/icons/lead-page/house";
import useGetLeadTracks from "@/modules/lead-page/hooks/get-lead-listings";
import PropertiesListingCard from "./properties-listing-card";
import PropertiesListingSkeleton from "./properties-listing-skeleton";
import { Box, useTheme } from "@mui/material";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import SectionHeader from "@/modules/lead-page/new/components/common/section-header";
import AddTrackDetailedForm from "@/modules/property-tracks/components/track-actions/add-track-detailed/form";
import { Plus } from "lucide-react";
import SquareButton from "@/modules/common/components/ui/square-button";
import DataTablePagination from "@/modules/common/components/tables/data-table/footer/pagination";
import { PaginationState } from "@tanstack/react-table";

interface PropertiesTabProps {
  onAddProperty?: () => void;
}

export const PropertiesTab: React.FC<PropertiesTabProps> = ({
  onAddProperty,
}) => {
  const { lead } = useContext(LeadPageContext);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { leadListings, isLoading, isError, count } = useGetLeadTracks({
    enabled: true,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });
  const [modalOpen, setModalOpen] = useState(false);

  const isLeadArchived = lead?.data?.lead?.archived;
  const customerUId = lead?.data?.lead?.customer_uid;
  const leadTypeUId = lead?.data?.lead_type?.id;
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <PropertiesListingSkeleton />
        <PropertiesListingSkeleton />
      </Box>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<HouseIcon />}
        label="Error"
        description="Error fetching properties."
      />
    );
  }

  if (
    !leadListings ||
    (leadListings.length === 0 && pagination.pageIndex === 0)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <SectionHeader title="Properties" count={0} />
        <EmptyState
          icon={<HouseIcon />}
          label="No properties yet"
          description="This lead has no properties yet."
          action={
            !isLeadArchived && customerUId ? (
              <SquareButton
                variant="ghost"
                onClick={() =>
                  onAddProperty ? onAddProperty() : setModalOpen(true)
                }
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: theme.nawy.colors.semantic.text.primary,
                  width: "fit-content",
                  padding: "4px 16px",
                  gap: "8px",
                }}
              >
                <Plus size={16} />
                Add Property
              </SquareButton>
            ) : undefined
          }
        />

        {customerUId && (
          <AddTrackDetailedForm
            customerUId={customerUId}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            leadTypeUId={leadTypeUId?.toString()}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        height: "100%",
        overflowY: "auto",
        position: "relative",
      }}
    >
      {leadListings?.map((listing) => (
        <ArchivedOverlay key={listing.uid} isArchived={isLeadArchived}>
          <PropertiesListingCard data={listing} />
        </ArchivedOverlay>
      ))}

      <Box
        sx={{
          mt: "auto",
          p: 2,
          borderTop: "1px solid",
          borderColor: theme.nawy.colors.semantic.border.secondary,
          bgcolor: theme.nawy.colors.semantic.layer.primary,
        }}
      >
        <DataTablePagination
          rowsCount={count || 0}
          calculatedPageCount={Math.ceil((count || 0) / pagination.pageSize)}
          pagination={pagination}
          setPagination={setPagination}
          pageSizeOptions={[10, 20, 50]}
        />
      </Box>

      {customerUId && (
        <AddTrackDetailedForm
          customerUId={customerUId}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          leadTypeUId={leadTypeUId?.toString()}
        />
      )}
    </Box>
  );
};

export default PropertiesTab;
